/**
 * Billing Transaction – Credit Bills.
 * GET /histopathology/billing/reports/credit-bill/?from_date&to_date
 *     [&include_paid_bill=true&is_bill_wise=true&export=pdf]
 * Response totals: total_amount, pending_amount.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Stack, Paper, Typography, TextField, IconButton, Tooltip, Button,
  FormControlLabel, Checkbox, Dialog, DialogTitle, DialogContent, DialogActions, Divider, Grid,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { DataGrid } from "@mui/x-data-grid";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DateTime } from "luxon";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { canEditTab } from "../../Utility/Services/RoleAccessService";
import { getCachevalue } from "../../Utility/Services/CacheProviderService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import CommonValidation from "../../Utility/CommonValidation";
import { CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import CommonDatePicker from "../../Components/Common Components/CommonDatePicker";
import { CurrencySymbol, TXN_MSG_MAX, TXN_MSG_MAX_LEN } from "../../Utility/Constants";
import "../../Styles/Configuration.css";

const today = () => DateTime.now().toFormat("yyyy-MM-dd");

const SummaryCard = ({ label, value }) => (
  <Paper elevation={1} sx={{ px: 2, py: 1.2, minWidth: "11vw", borderRadius: "8px" }}>
    <Typography sx={{ fontSize: "1.05vw", fontWeight: 700, color: "#1a1a1a" }}>{value}</Typography>
    <Typography sx={{ fontSize: "0.75vw", color: "#666" }}>{label}</Typography>
  </Paper>
);

export default function CreditBills() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [dateIndex, setDateIndex] = useState(1);
  const [search, setSearch] = useState("");
  const [totals, setTotals] = useState({ total: 0, pending: 0, paid: 0 });
  const [msg, setMsg] = useState({ err: false, errText: "", ok: false, okText: "" });
  const [payRow, setPayRow] = useState(null);
  const [payForm, setPayForm] = useState({
    amount: "",
    isCash: false, isCard: false, isUPI: false, isBank: false, isCheque: false,
    cash_amt: "", card_amt: "", upi_amt: "", bank_amt: "", cheque_amt: "",
    card_no: "", upi_no: "", bank_no: "", cheque_no: "",
  });
  const [paySaving, setPaySaving] = useState(false);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (fromDate) params.append("from_date", fromDate);
    if (toDate) params.append("to_date", toDate);
    params.append("is_bill_wise", "true");
    return params.toString();
  }, [fromDate, toDate]);

  const fetchData = useCallback(() => {
    setLoading(true);
    const url = `${Serviceurls.HISTOPATHOLOGY_BILLING_CREDIT_BILLS}?${buildQuery()}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setRows(res.data.data ?? []);
          const total = +res.data.total_amount || 0;
          const pending = +res.data.pending_amount || 0;
          setTotals({ total, pending, paid: total - pending });
        } else {
          setRows([]);
          setTotals({ total: 0, pending: 0, paid: 0 });
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        setTotals({ total: 0, pending: 0, paid: 0 });
        setMsg({ err: true, errText: err?.response?.data?.message || err?.message, ok: false, okText: "" });
      });
  }, [buildQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      [r.bill_number, r.patient_name, r.nxt_id, r.bill_type, r.corporate_name, r.created_by, r.patient_mobile]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const handleExportPdf = () => {
    const url = `${Serviceurls.HISTOPATHOLOGY_BILLING_CREDIT_BILLS}?${buildQuery()}&export=pdf`;
    RestAPIService.PrintPDF(url)
      .then((res) => {
        const file = new Blob([res.data], { type: "application/pdf" });
        window.open(URL.createObjectURL(file));
      })
      .catch(() => setMsg({ err: true, errText: "Unable to export PDF.", ok: false, okText: "" }));
  };

  const openPaymentPopup = (row) => {
    setPayRow(row);
    setPayForm({
      amount: String(row?.pending_amount ?? ""),
      isCash: false, isCard: false, isUPI: false, isBank: false, isCheque: false,
      cash_amt: "", card_amt: "", upi_amt: "", bank_amt: "", cheque_amt: "",
      card_no: "", upi_no: "", bank_no: "", cheque_no: "",
    });
  };

  const closePaymentPopup = () => {
    if (paySaving) return;
    setPayRow(null);
  };

  const totalEntered = useMemo(() => {
    return ["cash_amt", "card_amt", "upi_amt", "bank_amt", "cheque_amt"]
      .reduce((s, k) => s + (+payForm[k] || 0), 0);
  }, [payForm]);

  const handleAmountChange = (key) => (e) => {
    const value = e.target.value;
    if (value === "" || CommonValidation.DecimalNumber(value)) {
      const pending = +(payRow?.pending_amount ?? 0);
      const others = ["cash_amt", "card_amt", "upi_amt", "bank_amt", "cheque_amt"]
        .filter((k) => k !== key)
        .reduce((s, k) => s + (+payForm[k] || 0), 0);
      const allowed = +value + others;
      if (value === "" || allowed <= pending) {
        setPayForm((p) => ({ ...p, [key]: value }));
      }
    }
  };

  const refMandatory = useMemo(() => CommonValidation.isTransactionNumberMandatory(), []);

  useEffect(() => {
    if (!payRow) return;
    if (getCachevalue("auto_fetch_bill_amount_to_payment") !== "true") return;
    const pending = +(payRow?.pending_amount ?? 0);
    if (!pending) return;
    setPayForm((prev) => {
      const { isCash, isCard, isUPI, isBank, isCheque } = prev;
      const count = [isCash, isCard, isUPI, isBank, isCheque].filter(Boolean).length;
      if (count !== 1) return prev;
      return {
        ...prev,
        cash_amt: isCash ? String(pending) : prev.cash_amt,
        card_amt: isCard ? String(pending) : prev.card_amt,
        upi_amt: isUPI ? String(pending) : prev.upi_amt,
        bank_amt: isBank ? String(pending) : prev.bank_amt,
        cheque_amt: isCheque ? String(pending) : prev.cheque_amt,
      };
    });
  }, [
    payRow,
    payForm.isCash,
    payForm.isCard,
    payForm.isUPI,
    payForm.isBank,
    payForm.isCheque,
  ]);

  const checkReferenceValidation = () => {
    const cardAmt = +(payForm.card_amt || 0);
    const upiAmt = +(payForm.upi_amt || 0);
    const bankAmt = +(payForm.bank_amt || 0);
    const chequeAmt = +(payForm.cheque_amt || 0);
    const cardNo = (payForm.card_no || "").trim();
    const upiNo = (payForm.upi_no || "").trim();
    const bankNo = (payForm.bank_no || "").trim();
    const chequeNo = (payForm.cheque_no || "").trim();
    if (CommonValidation.isTransactionNumberMandatory()) {
      if (
        (payForm.isCard && cardAmt !== 0 && cardNo === "") ||
        (payForm.isUPI && upiAmt !== 0 && upiNo === "") ||
        (payForm.isBank && bankAmt !== 0 && bankNo === "") ||
        (payForm.isCheque && chequeAmt !== 0 && chequeNo === "")
      ) {
        return "Please, Enter Reference Number";
      }
    }
    if (
      (cardNo !== "" && cardNo.length < TXN_MSG_MAX_LEN) ||
      (upiNo !== "" && upiNo.length < TXN_MSG_MAX_LEN) ||
      (bankNo !== "" && bankNo.length < TXN_MSG_MAX_LEN) ||
      (chequeNo !== "" && chequeNo.length < TXN_MSG_MAX_LEN)
    ) {
      return TXN_MSG_MAX;
    }
    return null;
  };

  const handleReceiveSubmit = () => {
    const billId = payRow?.id ?? payRow?.bill_id;
    if (!billId) return;
    const pending = +(payRow?.pending_amount ?? 0);
    const hasMode = payForm.isCash || payForm.isCard || payForm.isUPI || payForm.isBank || payForm.isCheque;
    if (!hasMode) {
      setMsg({ err: true, errText: "Select at least one payment mode.", ok: false, okText: "" });
      return;
    }
    if (totalEntered <= 0) {
      setMsg({ err: true, errText: "Enter the amount to receive.", ok: false, okText: "" });
      return;
    }
    if (totalEntered > pending) {
      setMsg({ err: true, errText: "Entered amount exceeds pending balance.", ok: false, okText: "" });
      return;
    }
    const refMsg = checkReferenceValidation();
    if (refMsg) {
      setMsg({ err: true, errText: refMsg, ok: false, okText: "" });
      return;
    }
    const payload = {
      bill_id: billId,
      received_amount: CommonValidation.formatAmount(totalEntered),
      cash_amount: CommonValidation.formatAmount(payForm.isCash ? +payForm.cash_amt || 0 : 0),
      card_amount: CommonValidation.formatAmount(payForm.isCard ? +payForm.card_amt || 0 : 0),
      upi_amount: CommonValidation.formatAmount(payForm.isUPI ? +payForm.upi_amt || 0 : 0),
      bank_amount: CommonValidation.formatAmount(payForm.isBank ? +payForm.bank_amt || 0 : 0),
      cheque_amount: CommonValidation.formatAmount(payForm.isCheque ? +payForm.cheque_amt || 0 : 0),
      card_transaction_no: payForm.isCard ? String(payForm.card_no || "").trim() : "",
      upi_transaction_no: payForm.isUPI ? String(payForm.upi_no || "").trim() : "",
      bank_transaction_no: payForm.isBank ? String(payForm.bank_no || "").trim() : "",
      cheque_transaction_no: payForm.isCheque ? String(payForm.cheque_no || "").trim() : "",
    };
    setPaySaving(true);
    RestAPIService.create(payload, Serviceurls.HISTOPATHOLOGY_CREDIT_PAYMENT_RECEIVE)
      .then((res) => {
        setPaySaving(false);
        if (res?.data?.status === "success") {
          setPayRow(null);
          setMsg({ err: false, errText: "", ok: true, okText: res.data.message || "Payment received." });
          fetchData();
        } else {
          setMsg({ err: true, errText: res?.data?.message || "Failed to record payment.", ok: false, okText: "" });
        }
      })
      .catch((err) => {
        setPaySaving(false);
        setMsg({ err: true, errText: err?.response?.data?.message || err?.message, ok: false, okText: "" });
      });
  };

  const handlePrint = (row) => {
    const billId = row?.id ?? row?.bill_id;
    if (!billId) return;
    RestAPIService.PrintPDF(`${Serviceurls.HISTOPATHOLOGY_BILL_PRINT}?bill_id=${billId}`)
      .then((res) => {
        const file = new Blob([res.data], { type: "application/pdf" });
        window.open(URL.createObjectURL(file));
      })
      .catch(() => setMsg({ err: true, errText: "Unable to print bill.", ok: false, okText: "" }));
  };

  const columns = [
    {
      field: "patient_details", headerName: "Patient's Details", flex: 1.4, minWidth: 200, sortable: false,
      renderCell: (p) => {
        const sub = [p.row.nxt_id, p.row.patient_mobile].filter(Boolean).join(" | ") || "-";
        return (
          <Box sx={{ lineHeight: 1.3, minWidth: 0, width: "100%", overflow: "hidden" }}>
            <Tooltip title={(p.row.patient_name || "-").toUpperCase()} placement="top" arrow>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textTransform: "uppercase" }}
              >
                {p.row.patient_name || "-"}
              </Typography>
            </Tooltip>
            <Tooltip title={sub} placement="bottom" arrow>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}
              >
                {sub}
              </Typography>
            </Tooltip>
          </Box>
        );
      },
    },
    {
      field: "invoice_date", headerName: "Date", flex: 1.1, minWidth: 160,
      valueGetter: (value, row) =>
        row.invoice_date
          ? row.invoice_date
          : row.created_date ? DateTime.fromISO(row.created_date).toFormat("dd-MM-yyyy") : "-",
    },
    { field: "bill_number", headerName: "Bill No", flex: 0.8, minWidth: 120 },
    {
      field: "bill_type", headerName: "Bill Type", flex: 0.8, minWidth: 110,
      renderCell: (p) => {
        // Derive payment state from received vs payable so the second line reads
        // "Not Paid" / "Partial" / "Paid" instead of the raw backend status.
        const payable = +(p.row.payable_amount ?? p.row.total_amount ?? 0);
        const received = +(p.row.received_amount ?? 0);
        const pending = +(p.row.pending_amount ?? Math.max(payable - received, 0));
        let payState = "Not Paid";
        let payColor = "#d32f2f";
        if (payable > 0 && pending <= 0.005) {
          payState = "Paid";
          payColor = "#2e7d32";
        } else if (received > 0 && pending > 0) {
          payState = "Partial";
          payColor = "#ed6c02";
        }
        return (
          <Box sx={{ lineHeight: 1.3 }}>
            <Typography variant="body2" fontWeight={600}>{p.row.bill_type || "-"}</Typography>
            <Typography variant="caption" sx={{ color: payColor, fontWeight: 600 }}>
              {payState}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "corporate_name", headerName: "Credit Type", flex: 1.1, minWidth: 140,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3, minWidth: 0, width: "100%", overflow: "hidden" }}>
          <Typography
            variant="body2"
            sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {p.row.corporate_name ? "Hospital Credit" : "Patient Credit"}
          </Typography>
          {p.row.corporate_name && (
            <Tooltip title={p.row.corporate_name} placement="top" arrow>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {p.row.corporate_name}
              </Typography>
            </Tooltip>
          )}
        </Box>
      ),
    },
    {
      field: "payable_amount", headerName: `Total Bill (${CurrencySymbol})`, flex: 0.8, minWidth: 100,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.payable_amount ?? 0),
      renderCell: (p) => CommonValidation.AmountFormat(p.row.payable_amount ?? 0, false),
    },
    {
      field: "pending_amount", headerName: `Pending Amount (${CurrencySymbol})`, flex: 1, minWidth: 130,
      align: "right", headerAlign: "right", type: "number",
      valueGetter: (value, row) => +(row.pending_amount ?? 0),
      renderCell: (p) => (
        <Typography sx={{ fontSize: "0.85vw", color: +p.row.pending_amount > 0 ? "#d32f2f" : "#2e7d32", fontWeight: 600 }}>
          {CommonValidation.AmountFormat(p.row.pending_amount ?? 0, false)}
        </Typography>
      ),
    },
    {
      field: "action", headerName: "Action", sortable: false, flex: 0.7, minWidth: 100,
      align: "center", headerAlign: "center",
      renderCell: (p) => {
        const canEdit = canEditTab("Pending Bills");
        return (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title={!canEdit ? "No edit access" : (+p.row.pending_amount <= 0 ? "Fully paid" : "Receive Payment")}>
              <span>
                <IconButton
                  size="small"
                  disabled={!canEdit || +p.row.pending_amount <= 0}
                  onClick={() => openPaymentPopup(p.row)}
                >
                  <PaymentOutlinedIcon fontSize="small" color={(!canEdit || +p.row.pending_amount <= 0) ? "disabled" : "primary"} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Print">
              <IconButton size="small" onClick={() => handlePrint(p.row)}>
                <PrintOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  return (
    <Box sx={{ p: "0.8vw", height: "100%", display: "flex", flexDirection: "column", gap: "0.8vw", overflow: "hidden", minHeight: 0 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap">
        <SummaryCard label="Total Bill Amount" value={CommonValidation.AmountFormat(totals.total)} />
        <SummaryCard label="Total Paid Amount" value={CommonValidation.AmountFormat(totals.paid)} />
        <SummaryCard label="Total Pending Amount" value={CommonValidation.AmountFormat(totals.pending)} />
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ minWidth: "12vw" }}>
          <CommonDatePicker
            FromDate={fromDate}
            ToDate={toDate}
            DateIndex={dateIndex}
            HideAllMenu
            removeMarginRight
            SelectedDates={(from, to, idx) => {
              setFromDate(from);
              setToDate(to);
              setDateIndex(idx);
            }}
          />
        </Box>
        <TextField size="small" placeholder="Search" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: "14vw" }} />
        <Tooltip title="Export PDF">
          <Button variant="outlined" size="small" startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExportPdf} sx={{ textTransform: "capitalize" }}>
            PDF
          </Button>
        </Tooltip>
      </Stack>
      <Paper sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex" }}>
        <DataGrid
          rows={filtered.map((r, i) => ({ ...r, id: r.id ?? `row-${i}` }))}
          columns={columns}
          loading={loading}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
          pageSizeOptions={[10, 20, 30]}
          disableRowSelectionOnClick
          rowHeight={50}
          columnHeaderHeight={42}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => <Stack className="eMed_conf_nodata">No Records Found</Stack>,
          }}
          sx={{
            border: 0,
            flex: 1,
            minWidth: 0,
            "& .MuiDataGrid-scrollbar--horizontal, & .MuiDataGrid-scrollbarFiller--horizontal": {
              display: "none !important",
              height: "0 !important",
              visibility: "hidden !important",
            },
            "& .MuiDataGrid-footerContainer, & .MuiTablePagination-root, & .MuiTablePagination-toolbar": {
              overflow: "hidden !important",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              "&::-webkit-scrollbar": { display: "none !important", width: "0 !important", height: "0 !important" },
            },
          }}
        />
      </Paper>
      <Dialog open={!!payRow} onClose={closePaymentPopup} fullWidth maxWidth="sm">
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", pb: 1 }}>
          <Typography fontWeight={600}>Receive Payment</Typography>
          <IconButton size="small" onClick={closePaymentPopup} disabled={paySaving}><CloseIcon fontSize="small" /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 1.5 }}>
          {payRow && (
            <Stack spacing={1.2}>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Patient</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ textTransform: "uppercase" }}>{payRow.patient_name || "-"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Bill No</Typography>
                  <Typography variant="body2" fontWeight={600}>{payRow.bill_number || "-"}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Total</Typography>
                  <Typography variant="body2" fontWeight={600}>{CommonValidation.AmountFormat(payRow.payable_amount ?? 0)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Received</Typography>
                  <Typography variant="body2" fontWeight={600}>{CommonValidation.AmountFormat(payRow.received_amount ?? 0)}</Typography>
                </Grid>
                <Grid item xs={4}>
                  <Typography variant="caption" color="text.secondary">Pending</Typography>
                  <Typography variant="body2" fontWeight={600} color="error.main">{CommonValidation.AmountFormat(payRow.pending_amount ?? 0)}</Typography>
                </Grid>
              </Grid>

              <Divider />

              <Box>
                <Typography variant="body2" sx={{ mb: 0.5 }}>Payment Mode</Typography>
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {[
                    { key: "isCash", label: "Cash" },
                    { key: "isCard", label: "Card" },
                    { key: "isUPI", label: "UPI" },
                    { key: "isBank", label: "Bank" },
                    { key: "isCheque", label: "Cheque" },
                  ].map((m) => (
                    <FormControlLabel key={m.key}
                      control={<Checkbox size="small" checked={!!payForm[m.key]}
                        onChange={(e) => setPayForm((prev) => ({ ...prev, [m.key]: e.target.checked }))} />}
                      label={<Typography sx={{ fontSize: "0.85vw" }}>{m.label}</Typography>}
                    />
                  ))}
                </Stack>
              </Box>

              <Grid container spacing={1.5}>
                {payForm.isCash && (
                  <Grid item xs={12}>
                    <TextField fullWidth size="small" label="Cash Amount" value={payForm.cash_amt}
                      inputProps={{ inputMode: "decimal", maxLength: 12 }}
                      onChange={handleAmountChange("cash_amt")} />
                  </Grid>
                )}
                {payForm.isCard && (
                  <>
                    <Grid item xs={6}><TextField fullWidth size="small" label="Card Amount" value={payForm.card_amt}
                      inputProps={{ inputMode: "decimal", maxLength: 12 }}
                      onChange={handleAmountChange("card_amt")} /></Grid>
                    <Grid item xs={6}><TextField fullWidth size="small"
                      label={`Card Transaction No${refMandatory ? " *" : ""}`}
                      inputProps={{ maxLength: 30 }}
                      value={payForm.card_no}
                      onChange={(e) => setPayForm((p) => ({ ...p, card_no: e.target.value }))} /></Grid>
                  </>
                )}
                {payForm.isUPI && (
                  <>
                    <Grid item xs={6}><TextField fullWidth size="small" label="UPI Amount" value={payForm.upi_amt}
                      inputProps={{ inputMode: "decimal", maxLength: 12 }}
                      onChange={handleAmountChange("upi_amt")} /></Grid>
                    <Grid item xs={6}><TextField fullWidth size="small"
                      label={`UPI Transaction No${refMandatory ? " *" : ""}`}
                      inputProps={{ maxLength: 30 }}
                      value={payForm.upi_no}
                      onChange={(e) => setPayForm((p) => ({ ...p, upi_no: e.target.value }))} /></Grid>
                  </>
                )}
                {payForm.isBank && (
                  <>
                    <Grid item xs={6}><TextField fullWidth size="small" label="Bank Amount" value={payForm.bank_amt}
                      inputProps={{ inputMode: "decimal", maxLength: 12 }}
                      onChange={handleAmountChange("bank_amt")} /></Grid>
                    <Grid item xs={6}><TextField fullWidth size="small"
                      label={`Bank Reference No${refMandatory ? " *" : ""}`}
                      inputProps={{ maxLength: 30 }}
                      value={payForm.bank_no}
                      onChange={(e) => setPayForm((p) => ({ ...p, bank_no: e.target.value }))} /></Grid>
                  </>
                )}
                {payForm.isCheque && (
                  <>
                    <Grid item xs={6}><TextField fullWidth size="small" label="Cheque Amount" value={payForm.cheque_amt}
                      inputProps={{ inputMode: "decimal", maxLength: 12 }}
                      onChange={handleAmountChange("cheque_amt")} /></Grid>
                    <Grid item xs={6}><TextField fullWidth size="small"
                      label={`Cheque No${refMandatory ? " *" : ""}`}
                      inputProps={{ maxLength: 30 }}
                      value={payForm.cheque_no}
                      onChange={(e) => setPayForm((p) => ({ ...p, cheque_no: e.target.value }))} /></Grid>
                  </>
                )}
              </Grid>

              <Box display="flex" justifyContent="space-between" sx={{ pt: 0.5 }}>
                <Typography variant="body2" color="text.secondary">Total Receiving</Typography>
                <Typography variant="body2" fontWeight={700}>{CommonValidation.AmountFormat(totalEntered)}</Typography>
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button size="small" onClick={closePaymentPopup} disabled={paySaving}>Cancel</Button>
          <Button size="small" variant="contained" disableElevation onClick={handleReceiveSubmit} disabled={paySaving}>
            {paySaving ? "Saving..." : "Receive Payment"}
          </Button>
        </DialogActions>
      </Dialog>

      {msg.err && <ToastMsg severity="error" msg={msg.errText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
      {msg.ok && <ToastMsg severity="success" msg={msg.okText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
    </Box>
  );
}
